import axios from "axios";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { Datagrid, ListContextProvider, TextField, useList } from "react-admin";

import { Card, Typography } from "@mui/material";
import { GenericColorChip } from "shared/components/chips/GenericColorChip";
import { OptiTaskStatusColorMapping } from "shared/enums/colorMappings/optiColorMappings";

export default function OptiTasksList() {
  const [tasks, setTasks] = useState<any>(null);

  const BASE_URL = process.env.REACT_APP_OPTI_TASKS_API_URL;
  const url = `${BASE_URL}/tasks/datatable`;
  const START = 0;
  const LENGTH = 50;

  const formatTimestamps = useCallback((timestamp: number) => {
    return format(new Date(timestamp * 1000), "dd/MM/yyyy HH:mm:ss");
  }, []);

  const prepareData = useCallback(
    (tasksRaw: any) => {
      if (!tasksRaw) {
        return [];
      }
      return tasksRaw.map((task: any) => {
        return {
          ...task,
          received: formatTimestamps(task.received),
          started: formatTimestamps(task.started),
          succeeded: formatTimestamps(task.succeeded),
          runtime: task.runtime ? `${task.runtime.toFixed(2)} sec` : null,
        };
      });
    },
    [formatTimestamps],
  );

  const fetchTasks = useCallback(async () => {
    const payload2 = `draw=1&columns%5B0%5D%5Bdata%5D=name&columns%5B0%5D%5Bname%5D=&columns%5B0%5D%5Bsearchable%5D=true&columns%5B0%5D%5Borderable%5D=true&columns%5B0%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B0%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B1%5D%5Bdata%5D=uuid&columns%5B1%5D%5Bname%5D=&columns%5B1%5D%5Bsearchable%5D=true&columns%5B1%5D%5Borderable%5D=false&columns%5B1%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B1%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B2%5D%5Bdata%5D=state&columns%5B2%5D%5Bname%5D=&columns%5B2%5D%5Bsearchable%5D=true&columns%5B2%5D%5Borderable%5D=true&columns%5B2%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B2%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B3%5D%5Bdata%5D=args&columns%5B3%5D%5Bname%5D=&columns%5B3%5D%5Bsearchable%5D=true&columns%5B3%5D%5Borderable%5D=true&columns%5B3%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B3%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B4%5D%5Bdata%5D=kwargs&columns%5B4%5D%5Bname%5D=&columns%5B4%5D%5Bsearchable%5D=true&columns%5B4%5D%5Borderable%5D=true&columns%5B4%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B4%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B5%5D%5Bdata%5D=result&columns%5B5%5D%5Bname%5D=&columns%5B5%5D%5Bsearchable%5D=true&columns%5B5%5D%5Borderable%5D=true&columns%5B5%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B5%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B6%5D%5Bdata%5D=received&columns%5B6%5D%5Bname%5D=&columns%5B6%5D%5Bsearchable%5D=true&columns%5B6%5D%5Borderable%5D=true&columns%5B6%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B6%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B7%5D%5Bdata%5D=started&columns%5B7%5D%5Bname%5D=&columns%5B7%5D%5Bsearchable%5D=true&columns%5B7%5D%5Borderable%5D=true&columns%5B7%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B7%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B8%5D%5Bdata%5D=runtime&columns%5B8%5D%5Bname%5D=&columns%5B8%5D%5Bsearchable%5D=true&columns%5B8%5D%5Borderable%5D=true&columns%5B8%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B8%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B9%5D%5Bdata%5D=worker&columns%5B9%5D%5Bname%5D=&columns%5B9%5D%5Bsearchable%5D=true&columns%5B9%5D%5Borderable%5D=true&columns%5B9%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B9%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B10%5D%5Bdata%5D=exchange&columns%5B10%5D%5Bname%5D=&columns%5B10%5D%5Bsearchable%5D=true&columns%5B10%5D%5Borderable%5D=true&columns%5B10%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B10%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B11%5D%5Bdata%5D=routing_key&columns%5B11%5D%5Bname%5D=&columns%5B11%5D%5Bsearchable%5D=true&columns%5B11%5D%5Borderable%5D=true&columns%5B11%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B11%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B12%5D%5Bdata%5D=retries&columns%5B12%5D%5Bname%5D=&columns%5B12%5D%5Bsearchable%5D=true&columns%5B12%5D%5Borderable%5D=true&columns%5B12%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B12%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B13%5D%5Bdata%5D=revoked&columns%5B13%5D%5Bname%5D=&columns%5B13%5D%5Bsearchable%5D=true&columns%5B13%5D%5Borderable%5D=true&columns%5B13%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B13%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B14%5D%5Bdata%5D=exception&columns%5B14%5D%5Bname%5D=&columns%5B14%5D%5Bsearchable%5D=true&columns%5B14%5D%5Borderable%5D=true&columns%5B14%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B14%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B15%5D%5Bdata%5D=expires&columns%5B15%5D%5Bname%5D=&columns%5B15%5D%5Bsearchable%5D=true&columns%5B15%5D%5Borderable%5D=true&columns%5B15%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B15%5D%5Bsearch%5D%5Bregex%5D=false&columns%5B16%5D%5Bdata%5D=eta&columns%5B16%5D%5Bname%5D=&columns%5B16%5D%5Bsearchable%5D=true&columns%5B16%5D%5Borderable%5D=true&columns%5B16%5D%5Bsearch%5D%5Bvalue%5D=&columns%5B16%5D%5Bsearch%5D%5Bregex%5D=false&order%5B0%5D%5Bcolumn%5D=7&order%5B0%5D%5Bdir%5D=desc&start=${START}&length=${LENGTH}&search%5Bvalue%5D=&search%5Bregex%5D=false`;
    const response = await axios.post(url, payload2);
    const tasksRaw = response.data.data;
    setTasks(prepareData(tasksRaw));
  }, [url, prepareData]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);
  const listContext = useList({ data: tasks });

  if (!tasks) {
    return null;
  }
  return (
    <ListContextProvider value={listContext}>
      <Card
        sx={{
          mt: 2,
          p: 2,
        }}
      >
        <Typography variant="h6">Optimizer Tasks</Typography>
        {tasks.length ? (
          <Datagrid resource="books" data={tasks} total={LENGTH} bulkActionButtons={false}>
            <TextField source="name" />
            <GenericColorChip source="state" colorMapping={OptiTaskStatusColorMapping} />

            <TextField source="runtime" />
            <TextField source="received" />
            <TextField source="started" />
            <TextField source="succeeded" />
          </Datagrid>
        ) : (
          <Typography
            variant="body1"
            sx={{
              p: 8,
            }}
            align="center"
            fontStyle={"italic"}
          >
            No tasks found
          </Typography>
        )}
      </Card>
    </ListContextProvider>
  );
}
